import { getApiUrl, getApiToken, isServer } from "./env";
import { getTrackingData } from "./tracking";
import type { Media_Plain } from "../types/common/Media";

const LEGACY_STORAGE_PREFIXES = [
  "https://storagedtrading.blob.core.windows.net/media",
  "https://blobdc5websiteprodassets.blob.core.windows.net/media",
];
const NEW_STORAGE_PREFIX =
  "https://blobdc5websiteprodassets.blob.core.windows.net/container-dc5-website-prod-media";

// Get the url of the Strapi API
// On server: direct API call with token
// On client: use proxy endpoint (token stays on server)
export function getStrapiURL(path: string) {
  const apiUrl = getApiUrl();

  // On server: direct API call
  if (isServer()) {
    return `${apiUrl}${path}`;
  }

  // On client: use proxy endpoint (hides API URL and token)
  return `/api/proxy${path}`;
}

// Get API token (only available on server!)
function getApiTokenForRequest(): string {
  if (isServer()) {
    return getApiToken();
  }
  // On client, we don't have token - use proxy instead
  return "";
}

// get data from api
export function getStrapiResponse(path: string, method = "get") {
  const url = getStrapiURL(path);

  // On server: add token
  if (isServer()) {
    return fetch(url, {
      method: method,
      headers: new Headers({
        Authorization: "Bearer " + getApiTokenForRequest(),
      }),
    });
  }

  // On client: no token needed (proxy handles it)
  return fetch(url, {
    method: method,
    headers: {
      "Content-Type": "application/json",
    },
  });
}

// send data to api
export function sendStrapiRequest(path: string, data: object, method = "post") {
  const url = getStrapiURL(path);
  const enrichedData =
    path === "/api/forms" ? { ...data, ...getTrackingData() } : data;

  // On server: add token
  if (isServer()) {
    return fetch(url, {
      method: method,
      headers: {
        "Content-Type": "application/json",
        Authorization: "Bearer " + getApiTokenForRequest(),
      },
      body: JSON.stringify({ data: enrichedData }),
    });
  }

  // On client: no token needed (proxy handles it)
  return fetch(url, {
    method: method,
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ data: enrichedData }),
  });
}

// Media URLs must be identical on SSR and the first client render or React hydration
// fails (#418). Always use the same-origin proxy; the Node server implements /api/media.
export function getStrapiMedia(url: string | undefined): string | undefined {
  if (!url) {
    return undefined;
  }
  if (url.startsWith("http") || url.startsWith("//")) {
    return normalizeLegacyStorageUrl(url);
  }

  return `/api/media${url}`;
}

export function normalizeLegacyStorageUrl(url: string): string {
  if (!url) return url;
  for (const fromPrefix of LEGACY_STORAGE_PREFIXES) {
    if (url.startsWith(fromPrefix)) {
      return `${NEW_STORAGE_PREFIX}${url.slice(fromPrefix.length)}`;
    }
  }
  return url;
}

const FILE_ACCESS_PROXY_SUFFIX = "/api/file-access/proxy";

/**
 * Build a responsive <img> with srcSet + sizes, matching the DynamicPage/ESG pattern.
 * Uses imageMob (mobile), image (PC/default), imageRetina (large screens) if available.
 * When imageMob is not provided, falls back to image.formats.medium (≈750px) for mobile.
 * Wraps in <picture> with WebP <source> when WebP formats are available.
 * Falls back to a simple <img> when only the main image is provided.
 *
 * @param priority - If false (default), adds loading="lazy" for deferred loading.
 *                   If true, loads eagerly (for above-fold images like first slider).
 */
export function buildResponsiveImageSources(
  image: Media_Plain | undefined,
  imageMob: Media_Plain | undefined,
  imageRetina: Media_Plain | undefined,
  alt: string,
  priority: boolean = false,
): React.ReactElement {
  const pcUrl = getStrapiMedia(image?.url);
  // Fall back to image.formats.medium (≈750px) when no dedicated mobile image is uploaded
  const mobUrl = getStrapiMedia(imageMob?.url ?? image?.formats?.medium?.url);
  const retinaUrl = getStrapiMedia(imageRetina?.url);
  const webpUrl = getStrapiMedia(image?.formats?.webp?.url);
  const webpMobUrl = getStrapiMedia(
    imageMob?.formats?.webp?.url ?? image?.formats?.webp?.url,
  );
  const webpRetinaUrl = getStrapiMedia(imageRetina?.formats?.webp?.url);

  // Build srcSet: mob 767w, pc 1920w, retina 3840w.
  // Every candidate MUST carry a width descriptor — mixing a bare URL
  // (implicit density descriptor) invalidates the whole srcset in browsers.
  const srcSetParts: string[] = [];
  if (mobUrl) srcSetParts.push(`${mobUrl} 767w`);
  if (pcUrl) srcSetParts.push(`${pcUrl} 1920w`);
  if (retinaUrl) srcSetParts.push(`${retinaUrl} 3840w`);

  const srcSet = srcSetParts.length > 1 ? srcSetParts.join(", ") : undefined;
  const sizes = srcSet ? "(max-width: 767px) 767px, 100vw" : undefined;

  // Build WebP srcSet (same order)
  const webpSrcSetParts: string[] = [];
  if (webpMobUrl) webpSrcSetParts.push(`${webpMobUrl} 767w`);
  if (webpUrl) webpSrcSetParts.push(`${webpUrl} 1920w`);
  if (webpRetinaUrl) webpSrcSetParts.push(`${webpRetinaUrl} 3840w`);

  const webpSrcSet =
    webpSrcSetParts.length > 1 ? webpSrcSetParts.join(", ") : webpUrl;

  // Lazy loading for non-priority images
  const loading: "eager" | "lazy" = priority ? "eager" : "lazy";

  const sharedImgProps = {
    src: mobUrl || pcUrl,
    srcSet,
    sizes,
    alt,
    loading,
    decoding: "async" as const,
    ...(priority ? { fetchPriority: "high" as const } : {}),
  };

  // If WebP is available, wrap in <picture> for automatic format negotiation
  if (webpSrcSet) {
    return (
      <picture>
        <source srcSet={webpSrcSet} sizes={sizes} type="image/webp" />
        <img {...sharedImgProps} suppressHydrationWarning />
      </picture>
    );
  }

  return <img {...sharedImgProps} suppressHydrationWarning />;
}

function isFileAccessProxyPathname(pathname: string): boolean {
  return (
    pathname === FILE_ACCESS_PROXY_SUFFIX ||
    pathname.endsWith(FILE_ACCESS_PROXY_SUFFIX)
  );
}

/**
 * Fixes editor links that point at /api/file-access/proxy on the site origin
 * instead of the Strapi backend, and rewrites legacy blob URLs inside fileUrl.
 */
function normalizeFileAccessProxyHref(
  href: string,
  apiBaseUrl?: string,
): string {
  if (!href.includes("file-access/proxy")) {
    return href;
  }

  let url: URL;
  try {
    url = href.startsWith("http")
      ? new URL(href)
      : new URL(href, "https://local.invalid");
  } catch {
    return href;
  }

  if (!isFileAccessProxyPathname(url.pathname)) {
    return href;
  }

  const fileUrl = url.searchParams.get("fileUrl");
  if (fileUrl) {
    try {
      const decoded = decodeURIComponent(fileUrl);
      const normalizedFile = normalizeLegacyStorageUrl(decoded);
      if (normalizedFile !== decoded) {
        url.searchParams.set("fileUrl", normalizedFile);
      }
    } catch {
      // Keep original fileUrl if decoding fails.
    }
  }

  const pathAndQuery = `${url.pathname}${url.search}`;

  if (!href.startsWith("http")) {
    if (!apiBaseUrl) {
      return pathAndQuery;
    }
    return `${apiBaseUrl.replace(/\/$/, "")}${pathAndQuery}`;
  }

  if (!apiBaseUrl) {
    return url.toString();
  }

  let apiOrigin: string;
  try {
    apiOrigin = new URL(
      apiBaseUrl.endsWith("/") ? apiBaseUrl : `${apiBaseUrl}/`,
    ).origin;
  } catch {
    return url.toString();
  }

  if (url.origin !== apiOrigin) {
    return `${apiBaseUrl.replace(/\/$/, "")}${pathAndQuery}`;
  }

  return url.toString();
}

/**
 * Normalizes href values from Strapi Blocks content (regulatory accordion, etc.).
 * Only touches known legacy blob prefixes and file-access proxy patterns.
 */
export function normalizeContentLinkHref(
  href: string,
  options?: { apiBaseUrl?: string },
): string {
  if (!href) return href;
  if (href.startsWith("#protected:") || href.startsWith("javascript:")) {
    return href;
  }

  let result = normalizeLegacyStorageUrl(href);

  if (result.includes("file-access/proxy")) {
    result = normalizeFileAccessProxyHref(result, options?.apiBaseUrl);
  }

  return result;
}

// Build full URL for public Azure Blob assets (no secret token needed).
// Used to replace legacy hardcoded URLs in a few pages/components.
export function getBlobAssetUrl(
  fileOrPath: string | undefined,
): string | undefined {
  if (!fileOrPath) return undefined;

  // If it's already a full URL, keep it.
  if (fileOrPath.startsWith("http") || fileOrPath.startsWith("//")) {
    return fileOrPath;
  }

  // Extract file name (we expect inputs like "soc_1_xxx.svg" or paths).
  const fileName = fileOrPath.split("/").filter(Boolean).pop();
  if (!fileName) return fileOrPath;

  if (isServer()) {
    const storageAccount = process.env.STORAGE_ACCOUNT;
    const storageContainerName = process.env.STORAGE_CONTAINER_NAME;
    if (storageAccount && storageContainerName) {
      return `https://${storageAccount}.blob.core.windows.net/${storageContainerName}/assets/${fileName}`;
    }
    // Fallback to known production blob storage
    return `https://blobdc5websiteprodassets.blob.core.windows.net/container-dc5-website-prod-media/assets/${fileName}`;
  }

  const base = (window as Window & { __BLOB_ASSET_BASE__?: string })
    .__BLOB_ASSET_BASE__;
  if (base) return `${base}${fileName}`;

  // Fallback to known production blob storage
  return `https://blobdc5websiteprodassets.blob.core.windows.net/container-dc5-website-prod-media/assets/${fileName}`;
}

// fetch container
export async function fetchGlobalData(url: string) {
  const response = await getStrapiResponse(url);
  if (!response.ok) {
    throw new Error("Network response was not ok");
  }
  return response.json();
}

// add locale to url
export function addLocaleData(url: string | undefined, locale: string) {
  if (locale === "uk") {
    locale = "ua";
  }
  if (locale) {
    if (url && url.indexOf("//") > -1) {
      return url;
    }

    if (url && url.indexOf("/" + locale) > -1) {
      return url;
    }

    url = "/" + locale + url;
  }

  return url;
}

// add locale to url
export function getCurrentLocation(url: string) {
  if (url && url.indexOf("/ua") === 0) {
    url = url.substring(3);
  }

  if (url && url.indexOf("/en") === 0) {
    url = url.substring(3);
  }

  if (url && url.indexOf("/pl") === 0) {
    url = url.substring(3);
  }

  if (url && url.indexOf("/ro") === 0) {
    url = url.substring(3);
  }

  if (url && url.indexOf("/hr") === 0) {
    url = url.substring(3);
  }

  if (url && url.indexOf("/el") === 0) {
    url = url.substring(3);
  }

  if (url && url.indexOf("/bg") === 0) {
    url = url.substring(3);
  }

  if (url && url.indexOf("/lt") === 0) {
    url = url.substring(3);
  }

  if (url && url.indexOf("/it") === 0) {
    url = url.substring(3);
  }

  if (url && url.indexOf("/hu") === 0) {
    url = url.substring(3);
  }

  if (!url) {
    url = "/";
  }

  return url;
}
